import React, {useState} from 'react';
import {Box, Grid, Typography, Tabs, Tab} from '@mui/material';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PeopleIcon from '@material-ui/icons/People';
import SchoolIcon from '@material-ui/icons/School';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import NetworkDashboardShell from './NetworkDashboard.comp';
import {PageHeader, NoteBanner} from './widgets.comp';

const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const GREEN  = '#1B6B55';
const BLUE   = '#3B6FA0';

/* ── Reusable pieces ───────────────────────────────────────────── */
export const SectionHeader = ({eyebrow, title, subtitle}) => (
    <Box sx={{mb: 3}}>
        {eyebrow && (
            <Typography sx={{
                fontSize: '11px', fontWeight: 800, letterSpacing: '0.8px',
                textTransform: 'uppercase', color: ORANGE, mb: 0.5,
            }}>
                {eyebrow}
            </Typography>
        )}
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.25}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6}}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Tabbed page shell — used by every Overview sub-page
   ═════════════════════════════════════════════════════════════════ */
export const TabbedOverviewPage = ({tabs, skipNoDataGate = true}) => {
    const [activeTab, setActiveTab] = useState(0);
    const current = tabs[activeTab] || tabs[0];

    const tabsElement = tabs.length > 1 ? (
        <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
            sx={{
                borderBottom: '1px solid #E5E7EB',
                mb: 1,
                '& .MuiTab-root': {
                    textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2,
                    whiteSpace: 'nowrap', minHeight: 48, maxWidth: 'none',
                },
                '& .Mui-selected': {color: `${ORANGE} !important`},
            }}
        >
            {tabs.map((t) => <Tab key={t.label} label={t.label}/>)}
        </Tabs>
    ) : null;

    return (
        <NetworkDashboardShell skipNoDataGate={skipNoDataGate} tabs={tabsElement}>
            <Box sx={{mt: tabs.length > 1 ? 3 : 0}}>
                {current.content}
            </Box>
        </NetworkDashboardShell>
    );
};

/* ══════════════════════════════════════════════════════════════════
   About COS Readiness Solution — the 5-step baseline→action→verified-
   impact journey. Same step content as Partner/School Dashboard's own
   version (word-for-word — this is the same solution being described,
   the Network deck's slide 3 matches theirs exactly).
   ═════════════════════════════════════════════════════════════════ */
const READINESS_JOURNEY_STEPS = [
    {num: '1', title: 'COS Baseline', body: 'Measure your students’ online safety baseline through the COS assessment, covering online safety readiness, risk experiences, and context and support systems.'},
    {num: '2', title: 'Gap Diagnosis', body: 'Turn the results into priorities, so the weakest skills and items become a focused shortlist of where students need help most.'},
    {num: '3', title: 'Targeted Learning', body: 'Connect each priority to a concrete plan, pairing skill gaps with learning programs and broader needs with school or home support.'},
    {num: '4', title: 'Learning Impact', body: 'Track students’ real-time progress toward closing the priority gaps using pre and post evidence and activity evidence.'},
    {num: '5', title: 'COS Reassessment*', body: 'Repeat the COS assessment next cycle to verify which priority gaps have closed, so early progress becomes confirmed COS movement.'},
];

const JourneyStepCard = ({s, i, total}) => (
    <Box sx={{
        position: 'relative', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Box sx={{
            width: 34, height: 34, borderRadius: '50%', backgroundColor: GREEN,
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
            <Typography sx={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>{s.num}</Typography>
        </Box>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>{s.title}</Typography>
        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{s.body}</Typography>
        {i < total - 1 && (
            <Typography sx={{
                display: {xs: 'none', md: 'block'}, position: 'absolute', right: -14, top: '50%',
                transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: '22px', fontWeight: 800, zIndex: 1,
            }}>
                ›
            </Typography>
        )}
    </Box>
);

export const ReadinessSolutionOverviewSection = () => (
    <Box>
        <PageHeader title="How the COS Readiness Solution Works" subtitle="From baseline to action to verified impact"/>

        <Grid container spacing={2.5} alignItems="stretch">
            {READINESS_JOURNEY_STEPS.map((s, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={s.num}>
                    <JourneyStepCard s={s} i={i} total={READINESS_JOURNEY_STEPS.length}/>
                </Grid>
            ))}
        </Grid>

        <NoteBanner text="* COS Reassessment is available upon request in the next COS cycle."/>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   What Does the COS Children Assessment Measure? — a common slide, kept as
   static front-end text (Seongmin, 22 Sep). Source of truth: Ian's
   "Dashboard Common Section — Introduction & Methodology" deck (16 Sep),
   slide DB-CS-P0,P1-All-AboutCOSAssessment: 8 readiness areas, all 17
   child cyber-risk measures, 4 context & support areas.
   ═════════════════════════════════════════════════════════════════ */
const MeasureCard = ({letter, color, title, question, desc, badge, children}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
        display: 'flex', flexDirection: 'column',
    }}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
            <Box sx={{
                width: 42, height: 42, borderRadius: '50%', backgroundColor: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <Typography sx={{fontSize: '18px', fontWeight: 800, color: '#fff'}}>{letter}</Typography>
            </Box>
            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{title}</Typography>
        </Box>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1.5, minHeight: '72px'}}>
            <strong>{question}</strong>
            {desc && <><br/>{desc}</>}
        </Typography>
        <Box sx={{
            alignSelf: 'flex-start', backgroundColor: `${color}15`, color,
            fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: '6px', mb: 2,
        }}>
            {badge}
        </Box>
        <Box sx={{borderTop: '1px solid #F3F4F6', pt: 2, flex: 1}}>{children}</Box>
    </Box>
);

const READINESS_AREAS = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI-Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
];

const CYBER_RISK_GROUPS = [
    {category: null, items: ['Any Cyber Risk']},
    {category: 'CONDUCT RISK', items: ['Cyberbullying', 'Cybervictimization', 'AI Image and Voice Misuse Victimization']},
    {category: 'CONTENT RISK', items: ['Sexual Content Exposure', 'Violent Content Exposure', 'AI Misinformation and Deceptive Content Risk']},
    {category: 'CONTACT RISK', items: ['Offline Meetings with Online Strangers', 'Sexual Conversation', 'Unwanted Online Sexual Contact', 'AI-Generated Sexual Image Risk']},
    {category: 'CONTRACT & DATA RISK', items: ['Misuse of Personal Information', 'Underage Technology Use']},
    {category: 'WELLBEING & SCREEN USE RISK', items: ['Excessive Screen Time', 'Video Game Addiction Risk', 'Online Technology Addiction Risk', 'AI Emotional Reliance']},
];

const CONTEXT_AREAS = [
    'Digital Access at Home and School',
    'Youth Perception of Home and Support Environment',
    'Youth Perception of Parental Mediation',
    'Youth Perception of School AI-Digital Literacy Education',
];

export const WhatItMeasuresSection = () => {
    let cyberRiskCounter = 0;
    return (
        <Box>
            <SectionHeader
                title="What Does the Child Online Safety (COS) Children Assessment Measure?"
                subtitle="What do we need to understand to better support students in staying safe in the digital world?"
            />

            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="A" color={GREEN}
                        title="Child Online Safety Readiness"
                        question="Are students prepared?"
                        desc="Do they have the readiness to participate safely and responsibly in today's AI-powered digital world?"
                        badge="8 Areas"
                    >
                        <Box component="ol" sx={{m: 0, pl: 2.2}}>
                            {READINESS_AREAS.map((item) => (
                                <Box component="li" key={item} sx={{mb: 0.7, '&::marker': {color: GREEN, fontSize: '12.5px'}}}>
                                    <Typography sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5}}>{item}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="B" color={ORANGE}
                        title="Child Cyber-Risk Experiences *"
                        question="What risks are students facing?"
                        desc="How often do they report experiencing online risks in the past year?"
                        badge="17 Child Cyber-Risk Measures"
                    >
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.2, pl: 2.2}}>
                            {CYBER_RISK_GROUPS.map((group, gi) => (
                                <Box key={group.category || `main-${gi}`} sx={{mb: 0.5}}>
                                    {group.category && (
                                        <Typography sx={{
                                            fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase',
                                            color: ORANGE, mt: 1, mb: 0.4,
                                        }}>
                                            {group.category}
                                        </Typography>
                                    )}
                                    {group.items.map((item) => {
                                        cyberRiskCounter++;
                                        return (
                                            <Typography key={item} sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.6}}>
                                                {cyberRiskCounter}. {item}
                                            </Typography>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="C" color={BLUE}
                        title="Context & Support"
                        question="What supports do they have?"
                        desc="Do families and schools help students stay safe?"
                        badge="4 Areas"
                    >
                        <Box component="ol" sx={{m: 0, pl: 2.2}}>
                            {CONTEXT_AREAS.map((item) => (
                                <Box component="li" key={item} sx={{mb: 0.7, '&::marker': {color: BLUE, fontSize: '12.5px'}}}>
                                    <Typography sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5}}>{item}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>
            </Grid>

            <Box sx={{
                mt: 3, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3,
                fontSize: '13px', color: '#374151', lineHeight: 1.6, '& p': {m: 0, mb: 1, '&:last-child': {mb: 0}},
            }}>
                <p>* The risk definitions used in this assessment are consistent with the <strong>CPC Index global framework</strong>.</p>
                <p>The rubric tiers are based on each country’s percentile position within the 2017–2025 global reference distribution. National and global benchmark scores are aligned with the CPC Index methodology.</p>
                <p><strong>Any Cyber Risk</strong> refers to the proportion of children who experienced at least one of the cyber risks measured in the 2025 CPC Index, including content, contact, conduct, and contract risks including (Risks 2, 3, 5, 6, 8, 9, 10, 12, 13, 14, 15, 16).<br/>
                <strong>AI-related risks</strong> (Risks 4, 7, 11, 17) have been newly introduced in this assessment toolkit. Because sufficient historical and internationally comparable data are not yet available, national and global benchmark scores are not currently provided for these indicators.</p>
                <p><strong>CPC Index global benchmark:</strong> <a href="https://cpc.gcforum.org/global_cpc" target="_blank" rel="noreferrer">https://cpc.gcforum.org/global_cpc</a></p>
            </Box>
        </Box>
    );
};

/* ══════════════════════════════════════════════════════════════════
   About COS Children Assessment — "The COS Assessment Suite". Network's
   own deck slide (5) is a screenshot of this exact page on National
   Dashboard (image embedded in the slide, not editable text) — ported
   from National's own AssessmentSuiteSection fallback content/design
   (red-bordered highlight box, numbered badge + icon per card, hover
   lift), not Partner's differently-styled plain-card version this
   previously used.
   ═════════════════════════════════════════════════════════════════ */
const SUITE_STEPS = [
    {num: '1', title: 'Children',       icon: PersonOutlineIcon,  items: ['Child online safety readiness', 'Cyber risk experiences', 'Support and help-seeking environment']},
    {num: '2', title: 'Parents',        icon: PeopleIcon,         items: ['Digital parenting readiness', 'Home access and safety practices', 'Support network']},
    {num: '3', title: 'Teachers',       icon: SchoolIcon,         items: ['Risk Identification', 'Prevention and response', 'Classroom Child online safety education']},
    {num: '4', title: 'School leaders', icon: AccountBalanceIcon, items: ['Curriculum', 'Policies and governance', 'Reporting pathways and safeguarding practice']},
];

export const AssessmentSuiteSection = () => (
    <Box>
        <SectionHeader
            title="Child Online Safety (COS) Assessments"
            subtitle="The COS Assessment Suite includes four ready-to-deploy assessment packs that help networks establish a child online safety baseline, identify priority gaps, and benchmark performance against national and global reference points."
        />

        <Box sx={{
            border: '1.5px solid #FCA5A5', borderRadius: '16px', p: {xs: 2.5, md: 4},
            backgroundColor: 'var(--brand-surface-subtle, #FAFAFA)', boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
        }}>
            <Grid container spacing={3.5} justifyContent="center">
                {SUITE_STEPS.map((s) => {
                    const Icon = s.icon;
                    return (
                        <Grid item xs={12} sm={6} md={3} key={s.num}>
                            <Box sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.03)', p: 3, height: '100%',
                                display: 'flex', flexDirection: 'column',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.07)'},
                            }}>
                                <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2}}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '50%', backgroundColor: '#E8730C',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontSize: '14px', fontWeight: 'bold', flexShrink: 0, mt: 0.2,
                                    }}>
                                        {s.num}
                                    </Box>
                                    <Box sx={{flex: 1}}>
                                        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
                                            {s.title}
                                        </Typography>
                                        <Icon style={{fontSize: 40, color: '#9CA3AF'}}/>
                                    </Box>
                                </Box>

                                <Box sx={{flex: 1, borderTop: '1px solid #F3F4F6', pt: 2}}>
                                    <Box component="ul" sx={{m: 0, pl: 2.5}}>
                                        {s.items.map((it) => (
                                            <Box component="li" key={it} sx={{mb: 0.7}}>
                                                <Typography sx={{fontSize: '13.5px', color: '#4B5563', lineHeight: 1.6}}>{it}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            <Typography sx={{fontSize: '16px', fontWeight: 'bold', color: '#1E3A8A', textAlign: 'center', mt: 4, mb: 1}}>
                Each group receives role-specific assessments, scorecards, and dashboard views to support targeted action.
            </Typography>
        </Box>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   How COS Readiness Solution Scores Are Structured — static, no
   backend section yet. These 3 score types aren't sequential steps (no
   1→2→3 flow), so they're rendered as plain, unordered cards — a
   colored top accent per card instead of a numbered circle, and no
   chevron connector between them (per explicit instruction).
   ═════════════════════════════════════════════════════════════════ */
const READINESS_SOLUTION_SCORES = [
    {title: 'COS Baseline Score', color: GREEN, body: 'Defines initial COS readiness level and identifies priority areas for improvement.'},
    {title: 'Learning Impact Signal', color: ORANGE, body: 'Measures learning program impact using pre- and post-assessment evidence to show students’ progress in targeted COS areas.'},
    {title: 'COS Reassessment Score', color: BLUE, body: 'Confirms how much COS readiness has improved since the baseline assessment.'},
];

const ScoreTypeCard = ({title, color, body}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
    }}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.75}}>{title}</Typography>
            <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{body}</Typography>
        </Box>
    </Box>
);

export const ReadinessSolutionScoresSection = () => (
    <Box>
        <PageHeader title="How Child Online Safety (COS) Readiness Solution Scores Are Structured"/>

        <Grid container spacing={2.5} alignItems="stretch">
            {READINESS_SOLUTION_SCORES.map((s) => (
                <Grid item xs={12} sm={6} md={4} key={s.title}>
                    <ScoreTypeCard {...s}/>
                </Grid>
            ))}
        </Grid>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   How COS Children Assessment Scores Are Structured — static, no
   backend section yet. Same fallback content as Partner/School's own
   ScoreStructureSection (there, this is what renders when no S_2b
   dashboard section exists — Network has no card feed at all yet, so
   it's built static-only here rather than carrying the dead dynamic
   branch School's version has).
   ═════════════════════════════════════════════════════════════════ */
const SCORE_STEPS = [
    {num: '1', title: 'Questions',        body: 'What students answer in the survey.'},
    {num: '2', title: 'Item Scores',      body: 'Related answers are summarized into diagnostic topics.'},
    {num: '3', title: 'Area Scores',      body: 'Items are grouped into broader readiness, risk, and context areas.'},
    {num: '4', title: 'Composite Scores', body: 'Results show strengths, risks, and action priorities.'},
];
const SCORE_STATS = [
    {value: '179', label: 'Questions', body: 'Survey questions covering readiness, risks, and environment.'},
    {value: '37',  label: 'Items',     body: 'Diagnostic topics that combine related responses.'},
    {value: '12',  label: 'Areas',     body: 'Broader groups used to summarize child online safety readiness, risks, and context and supports.'},
];

export const ScoreStructureSection = () => (
    <Box>
        <PageHeader
            title="How COS Children Assessment Scores Are Structured"
            subtitle="COS Children Assessment scores are built step by step. Survey answers are summarized into item, area, and composite scores to help you identify strengths, risks, and action priorities."
        />

        <Grid container spacing={2.5} alignItems="stretch">
            {SCORE_STEPS.map((s, i) => (
                <Grid item xs={12} sm={6} md={3} key={s.num}>
                    <JourneyStepCard s={s} i={i} total={SCORE_STEPS.length}/>
                </Grid>
            ))}
        </Grid>

        <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mt: 4, mb: 2}}>
            Structures Used in the COS Children Assessment
        </Typography>
        <Grid container spacing={2.5} alignItems="stretch">
            {SCORE_STATS.map((st) => (
                <Grid item xs={12} sm={4} key={st.label}>
                    <Box sx={{
                        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1}}>
                            <Typography sx={{fontSize: '34px', fontWeight: 800, color: GREEN, lineHeight: 1}}>
                                {st.value}
                            </Typography>
                            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{st.label}</Typography>
                        </Box>
                        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{st.body}</Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   What This Report Tells You — static, no backend section yet.
   Word-for-word from the Dashboard Common Section deck's own School
   Network Dashboard (SND) slide — no audience-personalized phrasing
   ("network leaders" etc.), matching every other dashboard's plain
   "you" framing.
   ═════════════════════════════════════════════════════════════════ */
const REPORT_CARDS = [
    {
        barColor: '#1B6B55', label: 'This report helps you understand',
        items: [
            'How prepared students are to act safely and responsibly online.',
            'Which cyber risks students report experiencing.',
            'Which schools, grades or groups may need targeted support.',
            'Whether data coverage is strong enough to guide decisions.',
        ],
    },
    {
        barColor: '#3B6FA0', label: 'Use it to decide',
        items: [
            'See where each school is doing well and where students need more support.',
            'Spot the lowest area and item, and the schools that need follow-up.',
            'Which schools need more assessment coverage.',
            'Turn each decision question into a concrete next step with schools.',
        ],
    },
    {
        barColor: '#DC2626', label: 'Do not use it to',
        items: [
            'Label or blame students, teachers or schools.',
            'Treat context factors as performance.',
            'Compare small groups or schools without sufficient data.',
            'Read cyber-risk prevalence as a competency score.',
        ],
    },
];

export const UsingReportSection = () => (
    <Box>
        <PageHeader title="What This Report Tells You" subtitle="How should you use this report?"/>
        <Grid container spacing={3} alignItems="stretch" sx={{mt: 2}}>
            {REPORT_CARDS.map((card) => (
                <Grid item xs={12} md={4} key={card.label}>
                    <Box sx={{
                        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
                        minHeight: {md: '520px'}, display: 'flex', flexDirection: 'column',
                    }}>
                        <Box sx={{backgroundColor: card.barColor, height: '6px', flexShrink: 0}}/>
                        <Box sx={{p: {xs: 3, md: 4}, display: 'flex', flexDirection: 'column', flex: 1}}>
                            <Typography sx={{fontSize: '17px', fontWeight: 700, color: card.barColor, mb: 3, lineHeight: 1.3}}>
                                {card.label}
                            </Typography>
                            <Box component="ul" sx={{m: 0, pl: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5}}>
                                {card.items.map((item, i) => (
                                    <Box component="li" key={i}>
                                        <Typography sx={{fontSize: '14.5px', color: '#374151', lineHeight: 1.65}}>{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
);
